import React from 'react';
import {
  Row,
  Col,
  Select,
  TreeSelect,
  Popover,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { WarningOutlined, CopyOutlined } from '@ant-design/icons';
import { Buckets } from 'ontraccr-common';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { fieldOption, getContactDropdownOptions } from './formFieldsHelpers';
import OptionalRow from './OptionalRow';
import TitleRow from './TitleRow';

import SelectLabelFilter from './SelectLabelFilter';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import HoverHelp from '../../../common/HoverHelp';

import { getIdMap, binaryInsertSorted } from '../../../helpers/helpers';
import {
  formatFormTemplateName,
  getDescriptivePhaseCostcodeName,
  getPhaseCostcodeTreeData,
  getLinkId,
  formatFormDropdownList,
} from '../../formHelpers';
import { getLabelTypes } from '../../../settings/Labels/label.constants';
import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import FieldTriggerFlag from './FieldTriggerFlag';
import ConditionalRenderingRow from './ConditionalRenderingRow';
import Colors from '../../../constants/Colors';
import DropdownFieldSubDataTypeSelect from './DropdownFieldSubDataTypeSelect';
import DropdownFieldFilterCheckboxes from './DropdownFieldFilterCheckboxes';
import DropdownFieldStyleSelect from './DropdownFieldStyleSelect';
import DropdownFieldSelect from './DropdownFieldSelect';
import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import CustomerVendorSelector from '../../../common/inputs/CustomerVendorSelector';
import { getDropdownId } from '../../ResponderHelpers';
import { sortByObjProperty } from '../../../common/helpers';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import DropdownOptionCard from './DropdownOptionCard';
import { dataTypesAllowedInExternalForm, dataTypesAllowedInMultiDivisionDropdown } from '../../forms.constants';
import { formatEquipmentLabelFromCompanySettings } from '../../../equipment/equipment.helpers';
import FieldWidthSelector from './FieldWidthSelector';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';
import DropdownFieldPdfDisplaySelect from './DropdownFieldPdfDisplaySelect';

const canFilter = {
  Customers: true,
  Users: true,
  Vendors: true,
};

const enableGlobalSelectionSet = new Set(['Equipment']);
const shouldDisableSmartFilteringSet = new Set([
  'Customers',
  'Projects',
  'Costcodes',
  'CompletedForms',
  'Buckets',
  'PayableSubContracts',
]);

const getTypes = (t) => [
  {
    title: t('Customer', { count: 2 }),
    description: `User can select from existing ${t('Customer', { count: 2 })}`,
    value: 'Customers',
  },
  {
    title: t('Project', { count: 2 }),
    description: `User can select from existing ${t('Project', { count: 2 })}`,
    value: 'Projects',
    hasSubDataType: true,
    subDataTypePlaceholder: 'All',
  },
  {
    title: 'Users',
    description: 'User can select from existing Users',
    value: 'Users',
  },
  {
    title: 'Equipment',
    description: 'User can select from existing Equipment',
    value: 'Equipment',
    hasSubDataType: true,
    subDataTypePlaceholder: 'All',
  },
  {
    title: 'Vendors',
    description: 'User can select from existing Vendors',
    value: 'Vendors',
  },
  {
    title: 'Costcodes',
    description: 'User can select from existing Costcodes',
    value: 'Costcodes',
  },
  {
    title: 'Phases',
    description: 'User can select from existing Phases',
    value: 'Phases',
    mode: 'single',
  },
  {
    title: 'SoV Sub-Contracts',
    description: `User can select from a ${t('Project').toLowerCase()} SoV Sub-Contract`,
    value: 'Sub-Contracts',
  },
  {
    title: 'Contacts',
    description: `User can select a Contact from a Vendor/${t('Customer')} address Book`,
    value: 'Contacts',
  },
  {
    title: 'Custom',
    description: 'Add a list of options for the user to select from',
    value: 'Custom',
  },
  {
    title: 'Forms Workflows',
    description: 'User can select from existing Form Workflows',
    value: 'Forms',
  },
  {
    title: 'Labels',
    description: 'User can select from existing Labels',
    hasSubDataType: true,
    value: 'Labels',
  },
  {
    title: 'Form Submissions',
    description: 'User can select from existing Forms',
    hasSubDataType: true,
    value: 'CompletedForms',
    subDataTypePlaceholder: 'Select Form Workflow',
    mode: 'single',
    hasSubDataTypeFilter: true,
    subDataTypeFilterPlaceHolder: 'Select a status',
    subDataTypeFilterMode: 'multiple',
  },
  {
    title: 'Cards',
    description: 'User can select from existing Board Cards',
    hasSubDataType: true,
    subDataTypePlaceholder: 'Select Board',
    value: 'Cards',
    subDataTypeSelectMode: 'single',
  },
  {
    title: 'Boards',
    description: 'User can select a Board',
    value: 'Boards',
    mode: 'single',
  },
  {
    title: 'Buckets',
    description: 'User can select from existing Buckets',
    hasSubDataType: true,
    subDataTypePlaceholder: 'Select Bucket Type',
    value: 'Buckets',
    subDataTypeSelectMode: 'single',
  },
  {
    title: 'Sub-Contracts',
    description: 'User can select from existing Sub-Contracts',
    hasSubDataType: true,
    value: 'PayableSubContracts',
    subDataTypePlaceholder: 'Select Sub-Contract Form Workflow',
    mode: 'single',
    hasSubDataTypeFilter: true,
    subDataTypeFilterPlaceHolder: 'Select a status',
    subDataTypeFilterMode: 'multiple',
  },
  {
    title: 'Schedule Categories',
    description: 'User can select from existing Schedule Categories',
    value: 'ScheduleCategories',
    mode: 'single',
  },
  {
    title: 'Roles',
    description: 'User can select from existing User Roles',
    value: 'Roles',
  },
  {
    title: 'Divisions',
    description: 'User can select from existing Divisions',
    value: 'Divisions',
  },
];

const dataTypesThatRequireFetch = new Set(['CompletedForms', 'Cards', 'Buckets', 'PayableSubContracts']);

const configure = ({
  setConfigProps,
  configProps = {},
  isExternalForm,
  isMultiDivisionForm = false,
  sections = [],
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  t,
  projectTypes = [],
  equipmentTypes = [],
  id,
  labels = [],
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  contactAddressBooks = {},
  isExistingField = false,
  formStatuses = [],
  boards = [],
  cardTemplates = [],
  bucketTemplates = [],
  positions,
  buckets = [],
  scheduleCategories,
  isV3Form = false,
} = {}) => {
  let types = getTypes(t);
  const LABEL_TYPES = getLabelTypes(t);

  const {
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    selectMode,
    optional,
    dataType = '',
    openLimit,
    numAnswers = 1,
    customOptions: customOpts = [],
    subDataType,
    enableGlobalSelection,
    linkField,
    title,
    autoCollect,
    subDataTypeFilter = [],
    style,
    layout = 'vertical',
    positionIds,
    shouldDisableSmartFiltering = false,
    shouldUseCheckedInProject = false,
    enableEquipmentPrefillFromQR = false,
    customSort = false,
    filterToAssignedProjects = false,
    searchByProjectAddress = false,
    boardCardTypeFilter,
    defaultFilterLabels = [],
    lockedFilterLabels = [],
  } = configProps;

  let hasFormSelector = false;
  let hasSubContractSelector = false;

  sections?.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const { configProps: { dataType: fieldDataType, selectedType } = {} } = field;
      if (selectedType !== 'dropdown') return;
      if (fieldDataType === 'CompletedForms') hasFormSelector = true;
      if (fieldDataType === 'PayableSubContracts') hasSubContractSelector = true;
    });
  });

  // We only allow one choose one or more forms
  if (hasFormSelector && dataType !== 'CompletedForms') {
    types = types.filter((type) => type.value !== 'CompletedForms');
  }
  // We only allow one subcontract choose one or more
  if (hasSubContractSelector && dataType !== 'PayableSubContracts') {
    types = types.filter((type) => type.value !== 'PayableSubContracts');
  }

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    const newConfig = {
      ...configProps,
      title: value,
    };
    setConfigProps(newConfig);
  };

  const {
    hasSubDataTypeFilter,
    subDataTypeFilterPlaceHolder,
    subDataTypeFilterMode,
  } = types.find((type) => type.value === dataType) ?? {};

  const onSelect = (newDataType) => {
    const typeChanged = newDataType !== dataType;
    const ourType = types.find((type) => type.value === newDataType);
    const newConfig = {
      ...configProps,
      dataType: newDataType,
      hasSubDataType: ourType?.hasSubDataType,
      subDataTypePlaceholder: ourType?.subDataTypePlaceholder,
      enableGlobalSelection: enableGlobalSelectionSet.has(newDataType)
        ? enableGlobalSelection
        : false,
      selectMode: ourType?.mode,
      subDataTypeSelectMode: ourType?.subDataTypeSelectMode,
      linkField: typeChanged ? null : linkField,
      lockedFilterLabels: newDataType === 'Users' ? lockedFilterLabels : [],
    };

    if (newConfig.selectMode === 'single') {
      newConfig.numAnswers = 1;
      newConfig.openLimit = false;
    }

    setConfigProps(newConfig);
  };

  const onSubDataTypeSelect = (newSubDataType) => {
    const newConfig = {
      ...configProps,
      subDataType: newSubDataType,
    };

    setConfigProps(newConfig);
  };

  const onSubDataTypeFilter = (newSubDataTypeFilter) => {
    const newConfig = {
      ...configProps,
      subDataTypeFilter: newSubDataTypeFilter,
    };

    setConfigProps(newConfig);
  };

  const onNumberChange = (num) => {
    const newConfig = {
      ...configProps,
      numAnswers: num,
    };
    setConfigProps(newConfig);
  };

  const onCustomOptionSelect = (val) => {
    const added = { id: val, name: val };
    const next = customSort
      ? [...customOpts, added]
      : binaryInsertSorted(
        customOpts,
        added,
        (a, b) => a.name.localeCompare(b.name),
      );

    const newConfig = {
      ...configProps,
      customOptions: next,
    };

    if (next.length >= 5 && newConfig.style === 'radio') {
      newConfig.layout = 'vertical';
    }

    setConfigProps(newConfig);
  };
  const onCustomOptionDeselect = (val) => {
    const {
      customOptions = [],
    } = configProps;
    const newConfig = {
      ...configProps,
      customOptions: customOptions.filter((item) => item.id !== val),
    };
    setConfigProps(newConfig);
  };

  const onOptionDrag = (result) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;
    const draggedOption = customOpts[source.index];

    const newOptions = [...customOpts];
    newOptions.splice(source.index, 1);
    newOptions.splice(destination.index, 0, draggedOption);

    setConfigProps({
      ...configProps,
      customOptions: newOptions,
      customSort: true,
    });
  };

  const onCheckChanged = (key) => (checked) => {
    setConfigProps({
      ...configProps,
      [key]: checked,
    });
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const setConditionalRenderingFormula = (formula) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: formula,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setLinkField = (value) => {
    setConfigProps({
      ...configProps,
      linkField: value,
    });
  };

  const setPositionIds = (value) => {
    setConfigProps({
      ...configProps,
      positionIds: value,
    });
  };

  const setBoardCardTypeFilter = (value) => {
    setConfigProps({
      ...configProps,
      boardCardTypeFilter: value,
    });
  };

  const setDefaultFilterLabels = (newLabels) => {
    setConfigProps({
      ...configProps,
      defaultFilterLabels: newLabels,
    });
  };

  const setLockedFilterLabels = (newLabels = []) => {
    setConfigProps({
      ...configProps,
      lockedFilterLabels: newLabels,
    });
  };

  const setDropdownFieldStyle = (value) => {
    const newConfigProps = {
      ...configProps,
      style: value,
    };

    if (value === 'radio') {
      newConfigProps.openLimit = false;
      newConfigProps.numAnswers = 1;
      if (customOpts.length > 4) {
        newConfigProps.layout = 'vertical';
      }
    }

    if (value === 'dropdown') {
      newConfigProps.pdfDisplay = 'showSelected';
    }

    setConfigProps(newConfigProps);
  };

  const setDropdownFieldLayout = (value) => {
    setConfigProps({
      ...configProps,
      layout: value,
    });
  };

  const setWidth = (value) => {
    setConfigProps({
      ...configProps,
      width: value,
    });
  };

  const ourType = types.find((type) => type.value === dataType);
  const {
    hasSubDataType,
    subDataTypePlaceholder,
    subDataTypeSelectMode,
  } = ourType ?? configProps;

  const isCustom = dataType === 'Custom';
  const showAutoCollect = dataType === 'Projects' || dataType === 'Customers';

  let relevantTypes = types;

  if (isExternalForm) {
    relevantTypes = types.filter((type) => dataTypesAllowedInExternalForm.has(type.value));
  } else if (isMultiDivisionForm) {
    relevantTypes = types.filter((type) => dataTypesAllowedInMultiDivisionDropdown.has(type.value));
  }

  relevantTypes.sort(sortByObjProperty('title'));

  const relevantDivisions = new Set(divisions ?? []);

  const subDataTypeMap = {
    Labels: LABEL_TYPES,
    Equipment: equipmentTypes,
    Projects: projectTypes,
    CompletedForms: formTemplates
      .filter((ft) => (
        isBoardCards
        || ft.divisionIds?.some((divId) => relevantDivisions.has(divId))
        || relevantDivisions.has(ft.divisionId)
      ) && ft.active)
      .map((ft) => ({
        value: ft.id,
        label: formatFormTemplateName(ft, projectIdMap),
      })),
    Cards: boards.map((board) => ({
      value: board.id,
      label: board.title,
    })),
    Buckets: bucketTemplates.map((bucket) => ({
      value: bucket.id,
      label: bucket.name,
    })),
    PayableSubContracts: formTemplates
      .filter((ft) => (isBoardCards || relevantDivisions.has(ft.divisionId)) && ft.active && ft.type === 'Sub-Contract')
      .map((ft) => ({
        value: ft.id,
        label: formatFormTemplateName(ft, projectIdMap),
      })),
  };

  const subDataTypes = hasSubDataType
    ? subDataTypeMap[dataType].sort(sortByObjProperty('label'))
    : [];

  const formStatusOptions = Object.values(formStatuses).map((status) => ({
    value: status.id,
    label: status.status,
  })).sort(sortByObjProperty('label'));

  const subDataTypeFilterMap = {
    CompletedForms: formStatusOptions,
    PayableSubContracts: formStatusOptions,
  };

  const subDataTypeFilters = hasSubDataTypeFilter
    ? subDataTypeFilterMap[dataType]
    : [];

  const subDataTypeSet = new Set(subDataTypes?.map?.((type) => type.value));

  let filteredSubType;

  if ((subDataTypeSelectMode ?? selectMode) === 'single') {
    filteredSubType = subDataType;
  } else {
    filteredSubType = subDataType
      ?.filter?.((type) => subDataTypeSet.has(type)) ?? [];
  }

  const userLabelOptions = labels
    .filter((label) => label.type === 'users')
    .map((label) => ({ value: label.id, label: label.title }));

  const hasLockedFilterLabels = dataType === 'Users' && lockedFilterLabels.length > 0;

  const linkOptions = dataType
    ? (
      sections.reduce((acc, section) => {
        const { fields = [], name: sectionName } = section;
        return acc.concat(
          fields.reduce((fieldAcc, field) => {
            const {
              id: fieldId,
              selectedType,
              configProps: {
                dataType: fieldDataType,
                title: fieldTitle,
              } = {},
            } = field;
            if (selectedType !== 'dropdown') return fieldAcc;
            if (fieldDataType !== 'Customers' && fieldDataType !== 'Vendors') return fieldAcc;
            return fieldAcc.concat([{
              label: `${sectionName} - ${fieldTitle}`,
              value: fieldId,
            }]);
          }, []),
        );
      }, [])
    ) : [];

  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5, width: 350 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={title}
        />
      </Row>
      <OptionalRow onChange={onCheckChanged('optional')} optional={optional} disabled={disableOptional} />
      <ConditionalRenderingRow
        id={id}
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
        sections={sections}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        divisions={divisions}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      {
        enableGlobalSelectionSet.has(dataType)
        && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={enableGlobalSelection}
            onChange={onCheckChanged('enableGlobalSelection')}
            label="Enable Global Selection"
            hoverNode={(
              <div style={{ width: 250 }}>
                Check this box if you want Ontraccr to allow selecting all
                {' '}
                {dataType}
              </div>
            )}
          />
        )
      }
      {showAutoCollect && !isExternalForm && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={autoCollect}
          onChange={onCheckChanged('autoCollect')}
          label={`Pre-fill dropdown with location of ${t('Project').toLowerCase()}?`}
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want Ontraccr to automatically collect this data using the location of {t('Project').toLowerCase()}
            </div>
          )}
        />
      )}
      { dataType === 'Projects' && !isExternalForm && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={shouldUseCheckedInProject}
          onChange={onCheckChanged('shouldUseCheckedInProject')}
          label={`Pre-fill dropdown with clocked-in ${t('Project').toLowerCase()}?`}
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want this dropdown to be pre-filled with the clocked-in {t('Project').toLowerCase()}
            </div>
          )}
        />
      )}
      { dataType === 'Projects' && !isBoardCards && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={filterToAssignedProjects}
          onChange={onCheckChanged('filterToAssignedProjects')}
          label="Only display projects assigned to the user"
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want this dropdown to only show projects assigned to the user filling out the form
            </div>
          )}
        />
      )}
      { dataType === 'Projects' && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={searchByProjectAddress}
          onChange={onCheckChanged('searchByProjectAddress')}
          label={`Allow searching by ${t('Project').toLowerCase()} address?`}
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box to allow users to search for {t('Project', { count: 2 }).toLowerCase()} by their address
            </div>
          )}
        />
      )}
      { dataType === 'Equipment' && !isExternalForm && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={enableEquipmentPrefillFromQR}
          onChange={onCheckChanged('enableEquipmentPrefillFromQR')}
          label="Pre-fill dropdown with QR scanned equipment?"
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want this dropdown to be pre-filled with the equipment scanned from QR code
            </div>
          )}
        />
      )}
      { shouldDisableSmartFilteringSet.has(dataType) && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={shouldDisableSmartFiltering}
          onChange={onCheckChanged('shouldDisableSmartFiltering')}
          label="Disable Smart Filtering?"
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want this dropdown to be excluded from Smart Filtering
            </div>
          )}
        />
      )}
      <Row className="form-required-field" style={{ marginTop: 15 }}>
        <Col>
          Data Type:
        </Col>
        <Col>
          {isExistingField && (
            <Popover
              placement="bottomLeft"
              trigger="hover"
              content={(
                <div style={{ width: 200 }}>
                  Data type cannot be altered for existing forms.
                  You must create a new dropdown field
                  to use a different data type.
                </div>
              )}
              title="Warning"
              width={200}
            >
              <WarningOutlined
                style={{
                  color: Colors.ONTRACCR_DARK_YELLOW,
                  marginLeft: 10,
                }}
              />
            </Popover>
          )}
        </Col>
      </Row>
      <Row style={{ marginTop: 5 }}>
        <Select
          style={{ width: 350 }}
          placeholder="Select Data Type"
          onSelect={onSelect}
          showSearch
          optionFilterProp="label"
          value={dataType}
          disabled={isExistingField}
        >
          {
            relevantTypes.map((type) => (
              <Select.Option value={type.value} key={type.title} label={type.title}>
                {fieldOption(type)}
              </Select.Option>
            ))
          }
        </Select>
      </Row>
      {dataType === 'Contacts' && (
        <Row style={{ marginTop: 5 }}>
          <Select
            style={{ width: 350 }}
            placeholder="Link to a Customer/Vendor Field"
            defaultValue={null}
            value={linkField}
            optionFilterProp="label"
            onChange={setLinkField}
            options={[{ label: 'All', value: null }, ...linkOptions]}
            allowClear
          />
        </Row>
      )}
      {dataType === 'Users' && (
        <Row style={{ marginTop: 5 }}>
          <Select
            style={{ width: 350 }}
            placeholder="Roles"
            value={positionIds}
            optionFilterProp="label"
            onChange={setPositionIds}
            options={positions.map((pos) => ({ value: pos.id, label: pos.name }))}
            allowClear
            showSearch
            mode="multiple"
          />
        </Row>
      )}
      {dataType === 'Users' && (
        <Row style={{ marginTop: 5 }}>
          <Select
            style={{ width: 350 }}
            placeholder="Filter by User Labels"
            value={lockedFilterLabels}
            optionFilterProp="label"
            onChange={setLockedFilterLabels}
            options={userLabelOptions}
            allowClear
            showSearch
            mode="multiple"
          />
        </Row>
      )}
      {dataType === 'Boards' && (
        <Row style={{ marginTop: 5 }}>
          <Select
            style={{ width: 350 }}
            placeholder="Filter by Card Type (optional)"
            value={boardCardTypeFilter}
            optionFilterProp="label"
            onChange={setBoardCardTypeFilter}
            options={cardTemplates.map((template) => ({
              value: template.id,
              label: template.title,
            }))}
            allowClear
            showSearch
          />
        </Row>
      )}
      <DropdownFieldSubDataTypeSelect
        placeholder={subDataTypePlaceholder ?? `Select ${dataType} Type`}
        onChange={onSubDataTypeSelect}
        value={filteredSubType}
        mode={subDataTypeSelectMode ?? selectMode}
        disabled={isExistingField}
        options={subDataTypes}
      />
      <DropdownFieldSubDataTypeSelect
        placeholder={subDataTypeFilterPlaceHolder}
        onChange={onSubDataTypeFilter}
        value={subDataTypeFilter}
        mode={subDataTypeFilterMode}
        options={subDataTypeFilters}
      />
      {canFilter[dataType] && !hasLockedFilterLabels && (
        <DropdownFieldFilterCheckboxes
          labels={labels}
          dataType={dataType}
          defaultFilterLabels={defaultFilterLabels}
          onChange={setDefaultFilterLabels}
        />
      )}
      {
        isCustom
        && (
        <div>
          <Row className="form-required-field" style={{ marginTop: 15 }}>
            Custom options:
          </Row>
          <Row style={{ marginTop: 5, marginLeft: 0, marginRight: 0 }} gutter={10}>
            <Select
              style={{ width: 350 }}
              mode="tags"
              open={false}
              placeholder="Enter custom options"
              onSelect={onCustomOptionSelect}
              onDeselect={onCustomOptionDeselect}
              value={customOpts.map((opt) => opt.id)}
            />
          </Row>
          <DragDropContext onDragEnd={onOptionDrag}>
            <Droppable
              droppableId="customOptions"
              direction="vertical"
              ignoreContainerClipping
            >
              {({ innerRef, droppableProps, placeholder }) => (
                <div
                  ref={innerRef}
                  {...droppableProps}
                  style={{
                    width: '100%',
                  }}
                >
                  {customOpts.map((opt, idx) => (
                    <DropdownOptionCard
                      key={opt.id}
                      id={opt.id}
                      index={idx}
                      name={opt.name}
                      onDelete={onCustomOptionDeselect}
                    />
                  ))}
                  {placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

        </div>
        )
      }
      { !!isCustom && (
        <DropdownFieldStyleSelect
          value={style}
          onChange={setDropdownFieldStyle}
          layout={layout}
          onLayoutChange={setDropdownFieldLayout}
          disabled={customOpts.length >= 5 && style === 'radio'}
        />
      )}
      {!!isCustom && (
        <DropdownFieldPdfDisplaySelect
          isV3Form={isV3Form}
          configProps={configProps}
          setConfigProps={setConfigProps}
        />
      )}
      <Row style={{ marginTop: 15 }} gutter={10}>
        <Col>
          Number of answers:
        </Col>
        <Col>
          <HoverHelp placement="top" content="Configure how many options the user can select" />
        </Col>
      </Row>
      <Row style={{ marginTop: 5 }} align="middle" gutter={20}>
        <Col style={{ height: 32 }}>
          <OnTraccrNumberInput
            style={{ width: 100 }}
            value={numAnswers}
            min={1}
            max={autoCollect ? 1 : undefined}
            onChange={onNumberChange}
            disabled={autoCollect || openLimit || selectMode === 'single' || style === 'radio'}
          />
        </Col>
        <Col style={{ height: 32 }}>
          <OnTraccrCheckbox
            style={{ alignItems: 'center', height: '100%' }}
            value={openLimit}
            onChange={onCheckChanged('openLimit')}
            disabled={selectMode === 'single' || style === 'radio'}
            label="Leave open?"
            hoverNode="Check this box if you want to remove restrictions on the number of answers"
            hoverIconProps={{ style: { marginTop: 0 } }}
          />
        </Col>
      </Row>
      <FieldWidthSelector
        value={configProps.width}
        onChange={setWidth}
      />
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
};

function Preview({
  setPreviewProps,
  previewProps = {},
  configProps = {},
  projects = [],
  projectTypes = [],
  equipmentTypes = [],
  projectIdMap = {},
  users = [],
  customers = [],
  equipment = [],
  equipmentIdMap = {},
  vendors = [],
  vendorIdMap = {},
  costcodes = [],
  costcodeIdMap = {},
  costcodesLoading = false,
  phases = [],
  phaseIdMap = {},
  globalAddressBooks = {},
  contactAddressBooks = {},
  formTemplates = [],
  labels = [],
  projectEquipment = {},
  boardCardsForDropdown = {},
  bucketTemplateMap = {},
  id,
  responses,
  setResponses,
  projectId: formProjectId,
  customerId,
  vendorId,
  setBucketId,
  responding = false,
  userToLabel = {},
  customerToLabel = {},
  vendorToLabel = {},
  subContractMap = {},
  locked,
  templateId,
  sections,
  divisions,
  isDisplay,
  name,
  forms = [],
  subContractForms = [],
  parentForm,
  t,
  selectedBucket,
  selectedBucketTypeToIdMap,
  selectedBucketTypes,
  settings = {},
  isFormBuilder,
  setCustomerIds,
  setProjectIds,
  setVendorIds,
  setContactIds,
  setCostcodeIds,
  setUserIds,
  setSubContractIds,
  setPayableSubContractIds,
  dropdownFilterProjectIds,
  onFieldTrigger,
  equipmentId,
  setEquipmentId,
  phaseId,
  setPhaseId,
  scheduleCategories = [],
  isExternalForm,
  customerOptions = [],
  smartFilteredCustomers = [],
  projectOptions = [],
  smartFilteredProjects = [],
  customerIdMap = {},
  positionNames = [],
  divisionMap = {},
  boardsForDropdown = [],
  assignedProjectIds,
} = {}) {
  const {
    dataType,
    optional,
    title = 'Title goes here',
    numAnswers = 1,
    customOptions = [],
    openLimit,
    subDataType,
    enableGlobalSelection,
    fieldTrigger,
    linkField,
    style,
    layout = 'vertical',
    positionIds,
    shouldDisableSmartFiltering = false,
    filterToAssignedProjects = false,
    searchByProjectAddress = false,
    boardCardTypeFilter,
    isDynamicAttribute = false,
    defaultFilterLabels = [],
    lockedFilterLabels = [],
  } = configProps;
  const {
    selected: selectedProp = [],
    filterLabels = defaultFilterLabels,
  } = previewProps;

  // Admins can lock a dropdown to a set of User Labels.
  // When locked, responders cannot change the label filter.
  const hasLockedFilterLabels = dataType === 'Users' && lockedFilterLabels.length > 0;

  const selected = Array.isArray(selectedProp) ? selectedProp : [];

  const shouldLock = isDisplay || (
    (dataType === 'Projects' || dataType === 'Customers') && locked
  );

  const listResponses = responses && responses[id] && responses[id].values
    ? responses[id].values
    : [];

  const actualSelected = responding ? listResponses : selected;

  const onFilterChanged = (newFilterLabels = []) => {
    setPreviewProps({
      ...previewProps,
      filterLabels: newFilterLabels,
    });
  };

  const actualSelectedSet = new Set(
    actualSelected.map(getDropdownId),
  );

  const divSet = new Set(divisions);

  // Make sure to mirror changes in formFieldsHelpers.jsx::getDropDownOptions
  // TODO: Refactor to combine logic
  const linkedCustomerId = getLinkId({ responses, defaultValue: customerId, linkField });
  const linkedVendorId = getLinkId({ responses, defaultValue: vendorId, linkField });
  let data = [];
  let sortProperty = 'name';
  let allData = [];
  let placeholder = 'Select an option';

  const projectId = dropdownFilterProjectIds
    ? dropdownFilterProjectIds[0]
    : formProjectId;

  if (dataType === 'Customers') {
    if (shouldDisableSmartFiltering) {
      data = customerOptions;
    } else {
      data = smartFilteredCustomers;
    }
    data = data.filter((customer) => {
      const {
        [customer.id]: customerLabels = [],
      } = customerToLabel;
      const customerLabelSet = new Set(customerLabels);
      return filterLabels.every((label) => customerLabelSet.has(label));
    });
    placeholder = `Select a ${t('Customer')}`;
  } else if (dataType === 'Projects') {
    const projectTypeSet = new Set(projectTypes.map((type) => type.id));
    const subDataTypeSet = new Set(
      subDataType?.filter?.((typeId) => isExternalForm || projectTypeSet.has(typeId) || typeId === null) ?? [],
    );
    sortProperty = 'label';
    if (shouldDisableSmartFiltering) {
      data = projectOptions;
    } else {
      data = smartFilteredProjects;
    }
    data = data.filter((project) => (
      (!subDataTypeSet?.size || subDataTypeSet.has(project.projectTypeId))
      && (!filterToAssignedProjects || !assignedProjectIds || assignedProjectIds.has(project.id))
    ));
    placeholder = `Select a ${t('Project')}`;
  } else if (dataType === 'Users') {
    const userFilterLabels = hasLockedFilterLabels ? lockedFilterLabels : filterLabels;
    data = users.filter((user) => {
      const positionSet = new Set(positionIds ?? []);
      const {
        [user.id]: userLabels = [],
      } = userToLabel;
      const userLabelSet = new Set(userLabels);
      return (
        user.active
        && (
          actualSelectedSet.has(user.id)
          || (
            (
              userFilterLabels.length === 0
              || (
                hasLockedFilterLabels
                  ? userFilterLabels.some((label) => userLabelSet.has(label))
                  : userFilterLabels.every((label) => userLabelSet.has(label))
              )
            )
            && (positionSet.size === 0 || positionSet.has(user.positionId))
          )
        )
      );
    });
    placeholder = 'Select a User';
  } else if (dataType === 'Costcodes') {
    sortProperty = 'title';
    data = getPhaseCostcodeTreeData({
      projectId: shouldDisableSmartFiltering ? null : projectId,
      phaseId,
      phases,
      costcodes,
      costcodeMap: costcodeIdMap,
      projectMap: projectIdMap,
      disableOptions: !openLimit && numAnswers > 1 && actualSelected.length >= numAnswers,
      isClockIn: false,
    });
    placeholder = 'Select a Costcode';
  } else if (dataType === 'Equipment') {
    const equipmentTypeSet = new Set(equipmentTypes.map((type) => type.id));
    const subDataTypeSet = new Set(
      subDataType?.filter?.((typeId) => (
        isExternalForm || equipmentTypeSet.has(typeId) || typeId === null
      )) ?? [],
    );
    data = equipment.filter((eq) => (
      eq.active
        && eq.divisionIds.some((divisionId) => divSet.has(divisionId))
        && (enableGlobalSelection || !projectId || !!projectEquipment[projectId]?.[eq.id])
        && (!subDataTypeSet?.size || subDataTypeSet.has(eq.equipmentTypeId))
        && (enableGlobalSelection || !selectedBucket || Buckets.getLinkedBuckets([selectedBucket], [eq.id], ['equipment']).allBuckets.length > 0)
    )).map(({ name: eqName, code, ...rest }) => ({
      label: formatEquipmentLabelFromCompanySettings({ name: eqName, code, settings }),
      name: eqName,
      code,
      ...rest,
    }));
    sortProperty = 'label';
    placeholder = 'Select Equipment';
  } else if (dataType === 'Vendors') {
    data = vendors.filter((vendor) => {
      const {
        [vendor.id]: vendorLabels = [],
      } = vendorToLabel;
      const vendorLabelSet = new Set(vendorLabels);

      return (
        vendor.active
        && filterLabels.every((label) => vendorLabelSet.has(label))
        && vendor?.divisionIds?.some((divisionId) => divSet.has(divisionId))
      );
    });
    placeholder = 'Select Vendor';
  } else if (dataType === 'Sub-Contracts') {
    const {
      [projectId]: subcontracts = [],
    } = subContractMap;
    data = subcontracts
      .map((sub) => ({ id: sub.rowId, name: sub.description }));
  } else if (dataType === 'Contacts') {
    data = getContactDropdownOptions({
      contactAddressBooks,
      linkedCustomerId,
      linkedVendorId,
      responding,
      actualSelected,
      linkField,
      globalAddressBooks,
    });
    placeholder = 'Select Contact';
  } else if (dataType === 'Custom') {
    data = customOptions;
  } else if (dataType === 'Forms') {
    // Prevent infinite form workflow loop
    data = formTemplates.filter((ft) => ft.active && ft.id !== templateId);
    placeholder = 'Select Form Workflow';
  } else if (dataType === 'Labels') {
    const subDataTypeSet = new Set(subDataType);
    data = labels
      .filter((label) => !subDataType?.length || subDataTypeSet.has(label.type))
      .map((label) => ({ id: label.id, name: label.title }));
    placeholder = 'Select Label';
  } else if (dataType === 'CompletedForms') {
    data = formatFormDropdownList({
      formList: forms,
      projectIdMap,
      projectId,
      shouldFilterByProjects: !shouldDisableSmartFiltering,
    });
    placeholder = 'Select Form';
  } else if (dataType === 'Cards') {
    if (responding) {
      data = boardCardsForDropdown?.[subDataType]?.cards?.map((card) => ({
        id: card.id,
        name: card.name,
        subNames: [
          card.statusTitle ?? '',
          card.lastUpdated ? DateTime.fromMillis(card.lastUpdated).toLocaleString(DateTime.DATETIME_MED) : '',
        ],
      })) ?? [];
    } else {
      data = actualSelected ?? [];
    }
    placeholder = 'Select Card';
  } else if (dataType === 'Buckets') {
    const buckets = bucketTemplateMap?.[subDataType] ?? [];
    const filterIds = Buckets.getFilterIdsForBucketDropdown({
      buckets,
      customerId,
      projectId,
      selectedBucketTypeToIdMap,
      subDataType,
      equipmentId,
    });

    data = buckets;
    allData = buckets;

    if (!shouldDisableSmartFiltering && filterIds.length) {
      data = Buckets.getLinkedBuckets(buckets, filterIds, ['customers', 'projects', 'equipment', ...selectedBucketTypes]).allBuckets;
    }

    placeholder = 'Select Bucket';
  } else if (dataType === 'PayableSubContracts') {
    data = formatFormDropdownList({
      formList: subContractForms,
      projectIdMap,
      vendorIdMap,
      projectId,
      shouldFilterByProjects: !shouldDisableSmartFiltering,
    });
    placeholder = 'Select Sub-Contract';
  } else if (dataType === 'ScheduleCategories') {
    data = scheduleCategories;
    placeholder = 'Select Schedule Category';
  } else if (dataType === 'Roles') {
    data = positionNames.map((position) => ({
      id: position.id,
      name: position.name,
    }));
    placeholder = 'Select Role';
  } else if (dataType === 'Divisions') {
    // divisionMap already contains only divisions the user has access to
    data = Object.values(divisionMap)
      .map((division) => ({
        id: division.id,
        name: division.name,
      }));
    placeholder = 'Select Division';
  } else if (dataType === 'Boards') {
    data = boardsForDropdown
      .filter((board) => !boardCardTypeFilter || board.cardTypeId === boardCardTypeFilter)
      .map((board) => ({
        id: board.id,
        name: board.title,
      }));
    placeholder = 'Select Board';
  } else if (dataType === 'Phases') {
    data = projectId
      ? Object.values(
          getIdMap(phases.filter((phase) => phase.projectId === projectId))
        ).map((phase) => ({ id: phase.id, name: phase.name }))
      : [];
    placeholder = 'Select a Phase';
  }

  if (
    !configProps.customSort
    && (dataType !== 'Customers' && dataType !== 'Projects')
  ) {
    data = data.sort(sortByObjProperty(sortProperty));
  }
  let dataIdMap;
  if (dataType === 'Customers') {
    dataIdMap = customerIdMap;
  } else if (dataType === 'Projects') {
    dataIdMap = projectIdMap;
  } else {
    dataIdMap = getIdMap(data);
  }
  const showAllCustomOptionsInDisplay = isDisplay
    && dataType === 'Custom'
    && style !== 'dropdown';
  let options = responding || isFormBuilder || showAllCustomOptionsInDisplay ? data : selected;

  if (!responding && !isFormBuilder && (dataType === 'Projects' || dataType === 'Equipment')) {
    if (dataType === 'Projects') {
      options = selected.map((option) => {
        const {
          [option.id]: {
            name: projectName,
            number: projectNumber,
          } = {},
        } = projectIdMap ?? {};

        const label = formatProjectLabelFromCompanySettings({
          name: projectName,
          number: projectNumber,
          settings,
        });

        return {
          label,
          ...option,
        };
      });
    } else if (dataType === 'Equipment') {
      options = selected.map((option) => {
        const {
          [option.id]: {
            name: equipmentName,
            code: eqCode,
          } = {},
        } = equipmentIdMap ?? {};

        return {
          code: eqCode,
          name: equipmentName,
          ...option,
        };
      });
    }
  }

  let validData;
  if (dataType === 'Costcodes') {
    const allValidIds = [];
    if (responding) {
      options.forEach((phase) => {
        const { children = [] } = phase || {};
        children.forEach((costcode) => {
          if (!costcode.value) return;
          allValidIds.push(costcode.value);
        });
      });
    } else {
      options.forEach((selectedOption) => {
        const { id: selectedId } = selectedOption || {};
        if (selectedId) allValidIds.push(selectedId);
      });
    }
    validData = new Set(allValidIds);
  } else if (dataType === 'CompletedForms' && parentForm) {
    // getLightWeightForms takes some time so options is initially empty
    validData = new Set([parentForm.id]);
  } else {
    validData = new Set(options.map((datum) => datum.id));
  }

  const onSelectedChange = (newValue, newValueObject) => {
    let newSelected = newValue;

    // Radio Style Dropdown
    if (newValue?.target?.value) {
      newSelected = newValue.target.value;
    }
    const selectedIds = Array.isArray(newSelected) ? newSelected : [newSelected];
    const selectedObject = !Array.isArray(newValueObject) ? newValueObject : null;
    const values = selectedIds
      .filter((selectedId) => !!selectedId)
      .map((selectedId) => {
        if (dataType === 'Costcodes') {
          const descriptiveName = getDescriptivePhaseCostcodeName({
            projectId,
            selectedId,
            phaseMap: phaseIdMap,
            costcodeMap: costcodeIdMap,
          });
          return { id: selectedId, name: descriptiveName };
        }
        const {
          [selectedId]: {
            name: selectedName = selectedObject?.label,
          } = {},
        } = dataIdMap;
        return { id: selectedId, name: selectedName };
      });
    if (!responding) {
      setPreviewProps?.({
        ...previewProps,
        selected: values,
      });
      return;
    }
    const [{ id: firstId } = {}] = values;
    const valueIds = values?.map((val) => val.id);
    if (!shouldDisableSmartFiltering) {
      if (dataType === 'Customers' && setCustomerIds) {
        setCustomerIds(valueIds);
        if (setProjectIds && projectId && firstId) {
          const {
            [projectId]: { customerId: projectCustomerId } = {},
          } = projectIdMap;
          if (projectCustomerId !== firstId) setProjectIds();
        }
      } else if (dataType === 'Projects' && setProjectIds) {
        setProjectIds(valueIds);
      } else if (dataType === 'Vendors' && setVendorIds) {
        setVendorIds(valueIds);
      } else if (dataType === 'Contacts' && setContactIds) {
        setContactIds(valueIds);
      } else if (dataType === 'Costcodes' && setCostcodeIds) {
        setCostcodeIds(valueIds);
      } else if (dataType === 'Users' && setUserIds) {
        setUserIds(valueIds);
      } else if (dataType === 'Buckets' && setBucketId) {
        setBucketId(firstId);
      } else if (dataType === 'Sub-Contracts' && setSubContractIds) {
        setSubContractIds(valueIds);
      } else if (dataType === 'PayableSubContracts' && setPayableSubContractIds) {
        setPayableSubContractIds(valueIds);
      } else if (dataType === 'Equipment' && setEquipmentId) {
        setEquipmentId(firstId);
      } else if (dataType === 'Phases' && setPhaseId) {
        setPhaseId(firstId);
      }
    }
    setResponses((oldResponses) => ({
      ...oldResponses,
      [id]: {
        ...(oldResponses[id]),
        values,
      },
    }));
  };

  const validateSelectedItemsInFetchedData = (selectedItems, dataSource, fetchedData) => {
    const newSelectedItems = [];

    const dataSourceIdSet = new Set(dataSource.map((datum) => datum.id));
    const fetchedDataIdSet = new Set(fetchedData.map((datum) => datum.id));

    selectedItems?.forEach((selectedItem) => {
      const selectedId = getDropdownId(selectedItem);
      // If data is not fetched yet or it is already fetched, keep it
      if (
        !dataSourceIdSet.has(selectedId)
        || fetchedDataIdSet.has(selectedId)
      ) {
        newSelectedItems.push(selectedItem);
      }
    });

    if (selectedItems?.length !== newSelectedItems.length) {
      setTimeout(() => {
        onSelectedChange(newSelectedItems);
      });
    }
  };

  const actualSelectedIds = [];

  actualSelected.forEach((selectedItem) => {
    // Backwards compatible.
    // Versions <= 2.3.2 store dropdown values as strings, instead of objects
    const val = typeof selectedItem === 'string' ? selectedItem : selectedItem.id;
    if (val) {
      actualSelectedIds.push(val);
      actualSelectedSet.add(val);
    }
  });

  const shouldSkipSelectionValidation = isDisplay || isDynamicAttribute; // skip validation for dynamic attributes

  // Costcodes is intentionally not in dataTypesThatRequireFetch. That set only defers
  // validation until options?.length is truthy, but costcodes load in two staggered
  // requests (global + per-project). Global costcodes can populate options first,
  // which would trigger validation and clear phased/project-specific draft selections
  // before the project fetch finishes. costcodesLoading waits for both fetches instead.
  if (
    !shouldSkipSelectionValidation
    && (!dataTypesThatRequireFetch.has(dataType) || options?.length)
    && !(dataType === 'Costcodes' && costcodesLoading)
  ) {
    const invalidSelected = new Set(actualSelectedIds
      .filter((selectedId) => !validData.has(selectedId)));
    if (invalidSelected.size > 0) {
      // This usually happens when a dropdown is dependent on another dropdown
      // And that dropdown de-selects/selects another value (ie. customers and contact dropdown)
      // We need to wait for the other dropdown to update before updating this one
      // Or else we run into a race condition causing React to batch the updates
      setTimeout(() => {
        onSelectedChange(actualSelectedIds
          .filter((selectedId) => !invalidSelected.has(selectedId)));
      });
    }
  } else if (
    !shouldSkipSelectionValidation
    && dataType === 'Buckets'
    && allData?.length !== data?.length
  ) {
    // Buckets are fetched in a separate call
    // We need to wait for the data to be fetched
    // before checking to see if the selected items are valid
    validateSelectedItemsInFetchedData(
      actualSelected,
      allData,
      data,
    );
  }

  const onCopyClicked = async () => {
    // In display mode options is just selected values
    const copySource = showAllCustomOptionsInDisplay ? actualSelected : options;
    let textValue = '';
    if (dataType === 'Equipment') {
      textValue = copySource.map((option) => (
        formatEquipmentLabelFromCompanySettings({
          name: option.name,
          code: option.code,
          settings,
        })
      )).join(', ');
    } else {
      textValue = copySource.map((option) => option.label ?? option.name).join(', ');
    }
    await navigator.clipboard.writeText(textValue);
    message.success('Copied selected items');
  };

  const shouldDisable = !!parentForm && dataType === 'CompletedForms';

  let filterDisplay = null;
  if (isDisplay) {
    filterDisplay = (
      <BorderlessButton
        iconNode={<CopyOutlined />}
        style={{
          width: 'auto',
          height: 20,
          padding: 0,
        }}
        onClick={onCopyClicked}
      />
    );
  } else if (fieldTrigger) {
    filterDisplay = (
      <FieldTriggerFlag
        sections={sections}
        templateId={templateId}
        projectId={projectId}
        divisions={divisions}
        configProps={configProps}
        responding={responding}
        name={name}
        onFieldTrigger={onFieldTrigger}
      />
    );
  }

  const isFieldDisplay = !isFormBuilder && isDisplay;

  return (
    <div>
      <TitleRow
        title={title}
        optional={optional}
        filter={filterDisplay}
        isFieldDisplay={isFieldDisplay}
      />
      {responding && canFilter[dataType] && !hasLockedFilterLabels
        ? (
          <SelectLabelFilter
            selected={filterLabels}
            onSelectedChanged={onFilterChanged}
            style={{ margin: '15px 10px 5px 0px' }}
            type={dataType.toLowerCase()}
          />
        )
        : null}
      <Row style={{ marginTop: 10 }}>
        {(dataType !== 'Costcodes' && dataType !== 'Customers' && dataType !== 'Vendors') || !responding ? (
          <DropdownFieldSelect
            style={{ width: '100%', margin: '0px', pointerEvents: shouldLock ? 'none' : 'inherit' }}
            dropdownStyle={style}
            layout={layout}
            placeholder={placeholder}
            numAnswers={numAnswers}
            openLimit={openLimit}
            onChange={onSelectedChange}
            value={actualSelectedIds}
            disabled={shouldDisable}
            options={options}
            actualSelected={actualSelected}
            actualSelectedSet={actualSelectedSet}
            projectIdMap={projectIdMap}
            dataType={dataType}
            settings={settings}
            isExternalForm={isExternalForm}
            isFieldDisplay={isFieldDisplay}
            searchByProjectAddress={searchByProjectAddress}
          />
        ) : null}
        {(dataType === 'Customers' || dataType === 'Vendors') && responding ? (
          <CustomerVendorSelector
            style={{ width: '100%', margin: '0px', pointerEvents: shouldLock ? 'none' : 'inherit' }}
            options={data}
            value={actualSelectedIds}
            onChange={onSelectedChange}
            numAnswers={numAnswers}
            openLimit={openLimit}
            overrideDivisionIds={divisions}
            isVendor={dataType === 'Vendors'}
          />
        ) : null}
        {dataType === 'Costcodes' && responding ? (
          <TreeSelect
            allowClear
            showSearch
            loading={costcodesLoading}
            onChange={onSelectedChange}
            treeDefaultExpandAll={!projectId}
            treeNodeFilterProp="title"
            value={actualSelectedIds}
            placeholder={placeholder}
            multiple={numAnswers > 1 || openLimit}
            treeData={data}
            style={{ width: '100%', margin: '0px 10px', pointerEvents: shouldLock ? 'none' : 'inherit' }}
          />
        ) : null}
      </Row>
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
Preview.propTypes = {
  subContractForms: PropTypes.array,
  setCustomerIds: PropTypes.func.isRequired,
  setProjectIds: PropTypes.func.isRequired,
  setVendorIds: PropTypes.func.isRequired,
  setCostcodeIds: PropTypes.func.isRequired,
  setContactIds: PropTypes.func.isRequired,
  setUserIds: PropTypes.func.isRequired,
  setSubContractIds: PropTypes.func.isRequired,
  setPayableSubContractIds: PropTypes.func.isRequired,
  isExternalForm: PropTypes.bool,
  boardsForDropdown: PropTypes.array,
};

Preview.defaultProps = {
  subContractForms: [],
  isExternalForm: false,
};

export default {
  configure,
  preview: Preview,
  title: 'Choose one or more',
  description: 'User can choose one or more items from multiple options',
};
